import type {
  AuditEntry,
  Bucket,
  Comment,
  PersonForLogin,
  Photo,
  PhotoKind,
  Project,
  ProjectDetail,
  ProjectRef,
  SessionInfo,
  Status,
} from "../shared/types.ts";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code: string,
    /** Present on 429 rate-limit responses: seconds until the lock clears. */
    readonly retryAfter?: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, { credentials: "same-origin", ...init });
  const contentType = res.headers.get("content-type") ?? "";
  const body = contentType.includes("application/json") ? await res.json() : null;

  if (!res.ok) {
    throw new ApiError(
      res.status,
      body?.error ?? res.statusText ?? "Request failed",
      body?.code ?? "error",
      typeof body?.retry_after === "number" ? body.retry_after : undefined,
    );
  }
  return body as T;
}

const postJson = <T>(path: string, body: unknown) =>
  request<T>(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });

const patchJson = <T>(path: string, body: unknown) =>
  request<T>(path, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });

export interface ProjectQuery {
  bucket?: Bucket;
  status?: Status;
  area?: string;
  q?: string;
  includeDone?: boolean;
}

function projectQueryString(query: ProjectQuery): string {
  const params = new URLSearchParams();
  if (query.bucket) params.set("bucket", query.bucket);
  if (query.status) params.set("status", query.status);
  if (query.area) params.set("area", query.area);
  if (query.q) params.set("q", query.q);
  if (query.includeDone) params.set("include_done", "1");
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export const api = {
  /**
   * The pre-login "am I already signed in?" probe. A 401 here just means
   * "not signed in" — an expected, routine outcome on every fresh visit,
   * not a failure — so it resolves to `null` instead of rejecting.
   */
  me: async (): Promise<SessionInfo | null> => {
    try {
      return await request<SessionInfo>("/api/auth/me");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      throw err;
    }
  },
  login: (username: string, pin: string) => postJson<SessionInfo>("/api/auth/login", { username, pin }),
  logout: () => postJson<{ ok: true }>("/api/auth/logout", {}),

  /** The login screen's person picker. Unauthenticated. */
  peopleForLogin: () =>
    request<{ people: PersonForLogin[] }>("/api/people-for-login").then((r) => r.people),

  people: () =>
    request<{ people: { id: string; name: string; username: string; role: string }[] }>("/api/people").then(
      (r) => r.people,
    ),

  listProjects: (query: ProjectQuery = {}) =>
    request<{ projects: Project[] }>(`/api/projects${projectQueryString(query)}`).then((r) => r.projects),
  getProject: (id: string) =>
    request<{ project: ProjectDetail }>(`/api/projects/${id}`).then((r) => r.project),
  createProject: (input: { title: string; area?: string; bucket?: Bucket; notes_md?: string }) =>
    postJson<{ project: Project }>("/api/projects", input).then((r) => r.project),
  createProjectsBulk: (input: { area: string; bucket: Bucket; titles: string[] }) =>
    postJson<{ projects: Project[] }>("/api/projects/bulk", input).then((r) => r.projects),

  /** Distinct areas already used in this family — powers the bulk-add autocomplete. */
  areas: () => request<{ areas: string[] }>("/api/areas").then((r) => r.areas),
  updateProject: (
    id: string,
    patch: Partial<{ title: string; area: string; bucket: Bucket; status: Status; notes_md: string }>,
  ) => patchJson<{ project: Project }>(`/api/projects/${id}`, patch).then((r) => r.project),

  /** Sets the shared order for a bucket. 403s for anyone who isn't an adult. */
  reorderProjects: (bucket: Bucket, projectIds: string[]) =>
    patchJson<{ projects: Project[] }>("/api/projects/reorder", { bucket, project_ids: projectIds }).then(
      (r) => r.projects,
    ),

  addComment: (projectId: string, bodyMd: string) =>
    postJson<{ comment: Comment }>(`/api/projects/${projectId}/comments`, { body_md: bodyMd }).then(
      (r) => r.comment,
    ),

  addDependency: (projectId: string, blockerId: string) =>
    postJson<{ blocked_by: ProjectRef[] }>(`/api/projects/${projectId}/dependencies`, {
      blocked_by_project_id: blockerId,
    }).then((r) => r.blocked_by),
  removeDependency: (projectId: string, blockerId: string) =>
    request<{ blocked_by: ProjectRef[] }>(`/api/projects/${projectId}/dependencies/${blockerId}`, {
      method: "DELETE",
    }).then((r) => r.blocked_by),

  /** The project's history, newest first — who changed what, and when. */
  auditLog: (projectId: string) =>
    request<{ entries: AuditEntry[] }>(`/api/projects/${projectId}/audit`).then((r) => r.entries),

  uploadPhoto: (projectId: string, file: File, kind: PhotoKind = "normal") => {
    const form = new FormData();
    form.append("file", file);
    form.append("kind", kind);
    return request<{ photo: Photo }>(`/api/projects/${projectId}/photos`, {
      method: "POST",
      body: form,
    }).then((r) => r.photo);
  },
};

export const photoUrl = (photoId: string, variant: "thumb" | "display" | "original") =>
  `/api/photos/${photoId}/${variant}`;
